// Metrics tab — the time-series view of per-turn cost/latency/token metrics
// for this thread. Sourced from `GET .../turns` (the `persistent_agent_turn`
// table) for the rollup + history backfill, merged with the live SSE turns
// (`mergeTurns`) so the newest turn (including an in-flight 'running' one)
// appears instantly instead of waiting on the next 15s poll. Shares its React
// Query key with TracePanel so the two tabs dedupe the fetch.
import { useClient } from '@cortex/react-query';
import type { PersistentAgentTurn, PersistentAgentTurns } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';

import { formatDuration, formatRelative, formatTokens, formatUsd } from '../../lib/format';
import { mergeTurns } from '../../lib/use-persistent-agent-watch';

const turnStatusBadge: Record<string, string> = {
  running: 'badge badge--warning',
  finished: 'badge badge--success',
  error: 'badge badge--danger',
  aborted: 'badge badge--default',
};

// Scoped once per panel instance — cheap enough, and keeps the pulse CSS
// colocated with the only component that uses it instead of a shared sheet.
const pulseStyle = (
  <style>{`
    @keyframes pa-metrics-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
    .pa-metrics-running-dot {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--terminal-amber);
      animation: pa-metrics-pulse 1.2s ease-in-out infinite;
    }
  `}</style>
);

/** Small inline trend indicator — per-turn cost (falling back to duration when
 * a turn has no cost yet), oldest turn on the left, newest on the right. Not
 * meant to be read precisely; just a shape. */
function CostSparkline({ turns }: { turns: PersistentAgentTurn[] }) {
  const width = 120;
  const height = 28;
  const ordered = [...turns].reverse(); // mergeTurns is newest-first; plot oldest→newest.
  const values = ordered.map((t) => t.usd ?? t.durationMs ?? 0);
  if (values.length < 2) return <span className="t-xs t-muted">—</span>;
  const max = Math.max(...values, 1e-9);
  const stepX = width / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = height - 1 - (v / max) * (height - 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke="var(--terminal-amber)" strokeWidth={1.5} />
    </svg>
  );
}

export function MetricsPanel({
  agentId,
  threadId,
  liveTurns,
}: {
  agentId: string;
  threadId: string;
  liveTurns?: PersistentAgentTurn[];
}) {
  const client = useClient();
  const turns = useQuery({
    queryKey: ['persistent-agent-turns', threadId],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/turns`)
        .json<PersistentAgentTurns>(),
    refetchInterval: 15_000,
  });

  if (turns.isError) {
    return (
      <div className="alert alert-danger">
        {(turns.error as Error)?.message ?? 'Failed to load turn metrics'}
      </div>
    );
  }
  if (!turns.data) {
    return <span className="t-muted t-sm">Loading metrics…</span>;
  }

  const { rollup, turns: restTurns, sampledTurns } = turns.data;
  const list = mergeTurns(restTurns, liveTurns);
  if (list.length === 0) {
    return <span className="t-muted t-sm">No turns recorded yet.</span>;
  }
  // Bars scale against the slowest observed turn duration, mirroring
  // ToolUsagePanel's bar scaling so the two tabs read consistently.
  const scaleMs = Math.max(...list.map((t) => t.durationMs ?? 0), 1);

  // Cache tokens + share are computed over the sampled/live window (`list`),
  // distinct from `rollup`'s lifetime totals — labelled "(recent)" below.
  let cacheReadSum = 0;
  let cacheWriteSum = 0;
  let tokensInSum = 0;
  for (const turn of list) {
    cacheReadSum += turn.cacheReadTokens;
    cacheWriteSum += turn.cacheWriteTokens;
    tokensInSum += turn.tokensIn;
  }
  const cacheReadSharePct =
    tokensInSum > 0 ? `${((cacheReadSum / tokensInSum) * 100).toFixed(1)}%` : null;
  const errorRatePct =
    rollup.totalToolCalls > 0
      ? `${((rollup.totalToolErrors / rollup.totalToolCalls) * 100).toFixed(1)}%`
      : '0%';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {pulseStyle}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
          padding: '8px 10px',
          background: 'rgba(232,171,127,0.06)',
          border: '1px solid rgba(232,171,127,0.15)',
          borderRadius: 'var(--radius-sm, 3px)',
        }}
      >
        <div>
          <div className="t-xs t-muted">Total turns</div>
          <div className="t-sm t-mono">{rollup.totalTurns}</div>
        </div>
        <div>
          <div className="t-xs t-muted">Total cost</div>
          <div className="t-sm t-mono">{formatUsd(rollup.totalUsd)}</div>
        </div>
        <div>
          <div className="t-xs t-muted">Tokens in/out</div>
          <div className="t-sm t-mono">
            {formatTokens(rollup.totalTokensIn)} / {formatTokens(rollup.totalTokensOut)}
          </div>
        </div>
        <div>
          <div className="t-xs t-muted">Avg turn duration</div>
          <div className="t-sm t-mono">{formatDuration(rollup.avgDurationMs)}</div>
        </div>
        <div>
          <div className="t-xs t-muted">Tool calls</div>
          <div className="t-sm t-mono">{rollup.totalToolCalls}</div>
        </div>
        <div>
          <div className="t-xs t-muted">Tool errors</div>
          <div className="t-sm t-mono">
            {rollup.totalToolErrors} <span className="t-muted">({errorRatePct} error rate)</span>
          </div>
        </div>
        <div>
          <div className="t-xs t-muted">Cache r/w (recent)</div>
          <div className="t-sm t-mono">
            {formatTokens(cacheReadSum)} / {formatTokens(cacheWriteSum)}
            {cacheReadSharePct && (
              <span className="t-xs t-muted"> · {cacheReadSharePct} of tokens in</span>
            )}
          </div>
        </div>
        <div>
          <div className="t-xs t-muted">Cost trend (recent)</div>
          <CostSparkline turns={list} />
        </div>
      </div>
      {sampledTurns < rollup.totalTurns && (
        <span className="t-xs t-muted">stats over the last {sampledTurns} turns</span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {list.map((turn) => {
          const running = turn.status === 'running';
          const durationPct =
            turn.durationMs != null ? Math.max((turn.durationMs / scaleMs) * 100, 1) : 0;
          return (
            <div
              key={turn.id}
              style={{
                padding: '8px 12px',
                background: 'rgba(232,171,127,0.04)',
                border: '1px solid rgba(232,171,127,0.1)',
                borderRadius: 'var(--radius-sm, 3px)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                }}
              >
                {running && <span className="pa-metrics-running-dot" />}
                <span
                  className={turnStatusBadge[turn.status] ?? 'badge badge--neutral'}
                  style={{ fontSize: 9 }}
                >
                  {turn.status}
                </span>
                <span className="t-xs t-muted">{formatRelative(turn.startedAt)}</span>
                <span
                  className="t-xs"
                  style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}
                >
                  {running ? (
                    'running…'
                  ) : (
                    <>
                      {formatDuration(turn.durationMs)} · {formatUsd(turn.usd)} ·{' '}
                      {formatTokens(turn.tokensIn)}/{formatTokens(turn.tokensOut)} tok ·{' '}
                      {turn.toolCalls} tool{turn.toolCalls === 1 ? '' : 's'}
                      {turn.toolErrors > 0 ? ` (${turn.toolErrors} err)` : ''}
                    </>
                  )}
                </span>
              </div>
              {!running && (
                <div
                  style={{
                    position: 'relative',
                    height: 4,
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    marginTop: 7,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: `${durationPct}%`,
                      background: 'var(--terminal-amber)',
                      borderRadius: 2,
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
